import {
  IsEmailVerifiedData,
  IsEmailVerifiedVars,
  IsUsernameAvailableData,
  IsUsernameAvailableVars,
  VerifyRecaptchaTokenData,
  VerifyRecaptchaTokenVars
} from '../gql-types';

import ApolloClientService from '../../shared/services/ApolloClientService';
import AuthService from '../../shared/services/AuthService';
import Config from '../../shared/config';
import { PublicGraphQL } from '../gql';
import User from '../models/User';
import axios from 'axios';

class MembershipService {
  /** GraphQL client for MembershipDS. */
  private publicClient = ApolloClientService.create(
    Config.MEMBERSHIP_PUBLIC_GQL
  );

  /**
   * Returns a promise that is resolved with a value indicating whether a
   * username (typically email address) is available in the tenant.
   * @param tenant Tenant where to check the username.
   * @param login Username or email to check.
   */
  public async isUsernameAvailable(
    tenant: string,
    login: string
  ): Promise<boolean> {
    const { data } = await this.publicClient.query<
      IsUsernameAvailableData,
      IsUsernameAvailableVars
    >({
      query: PublicGraphQL.Query.IS_USERNAME_AVAILABLE,
      variables: { tenant, login }
    });
    return data.isUsernameAvailable;
  }

  /**
   * Returns a promise that is resolved with a value indicating whether an
   * email address is verified in the tenant.
   * @param tenant Tenant where to check the username.
   * @param email Email to check.
   */
  public async isEmailVerified(
    tenant: string,
    email: string
  ): Promise<boolean> {
    const { data } = await this.publicClient.query<
      IsEmailVerifiedData,
      IsEmailVerifiedVars
    >({
      query: PublicGraphQL.Query.IS_EMAIL_VERIFIED,
      variables: { tenant, email }
    });
    return data.isEmailVerified;
  }

  public async getUserByKeyAsync(userkey: number) {
    const accessToken = AuthService.getAccessToken();
    const { data } = await axios.get<User>(
      `${Config.MEMBERSHIP_DS}/api/users/${userkey}`,
      {
        headers: {
          authorization: `Bearer ${accessToken}`
        }
      }
    );

    return data;
  }

  public async getVerifyRecaptchaToken(token: string): Promise<boolean> {
    const { data } = await this.publicClient.mutate<
      VerifyRecaptchaTokenData,
      VerifyRecaptchaTokenVars
    >({
      mutation: PublicGraphQL.Mutation.VERIFY_RECAPTCHA_TOKEN,
      variables: { token }
    });
    return data.verifyRecaptchaToken;
  }

  //#region Avatar.
  /**
   * Returns the URL pointing to the given user's avatar.
   * @param userkey Identifier of user whose avatar to retrieve.
   */
  public async getAvatarUrl(userkey: number): Promise<string | undefined> {
    const url = `${Config.ASSET_DS}/api/public/avatar/${userkey}`;
    try {
      const { data } = await axios.get(url, {
        headers: { authorization: `Bearer ${AuthService.getAccessToken()}` }
      });
      return data;
    } catch (err) {
      // NOP - No avatar issues 404.
    }
    return undefined;
  }

  /**
   * Set the avatar image path for the current user.
   * @param assetpath Path to set the avatar to.
   */
  public async setAvatarUrl(assetpath: string): Promise<void> {
    const url = `${Config.ASSET_DS}/api/public/avatar`;
    await axios.post(
      url,
      { assetpath },
      {
        headers: { authorization: `Bearer ${AuthService.getAccessToken()}` }
      }
    );
  }

  /**
   * Delete the current user's avatar on the server.
   */
  public async removeAvatar(): Promise<void> {
    await axios.delete(`${Config.ASSET_DS}/api/public/avatar`, {
      headers: { authorization: `Bearer ${AuthService.getAccessToken()}` }
    });
  }
  //#endregion
}

export default new MembershipService();
